import type { World } from '../sim/types';
import { civilFromEpochDay, dateLabel, epochDayFromCivil, epochDayOf } from '../sim/calendar';
import { ISLAND_NAMES, REEF_ISLANDS, type ReefIsland } from '../sim/reef';
import { ASHORE_T_PER_KM_YEAR, CLEARED_T_PER_DAY, shoreDebrisT } from '../sim/shoreDebris';
import { COASTLINE_KM } from '../sim/turtles';
import { HEAT_ALERT_DHW, MONTH_DAYS, consultationCost, dollars, freeLiaison, protectionJob, surveyCost } from '../sim/operations';
import { degreeHeatingWeeks } from '../sim/heatStress';
import { grazingCaughtKgMonth, roughly } from './actionWorth';
import { SHORE_DAYS, SURVEY_DAYS, goalsOf, inTutorial, mostExposed, nextStretch, planRoom } from './goals';
import { currentObjective } from '../sim/campaign';
import { escapeHtml } from './format';
import { coralVerdict } from './reefHealth';
import { spareWords, yearMoney } from './runway';
import { stepFromFindings } from './steer';
import { managementOf } from './overview';
import { patrolMonthly } from './areasPanel';

/**
 * The month in the field (M322, the casual year's H4, the main session's playtest: "Nothing happens unless the player
 * makes it happen"; the month's own review: "No survey came home and no rule was made this month"): what the month did
 * on the water, read from the world's own figures, and one decision it puts to the player, with what it costs and what
 * letting it be leaves. GAME CHOICE: on the month's card, which already holds the clock (M289), so a decision comes each
 * month the player keeps the cards; no event the world does not hold, no dice of the interface's own.
 */

/** The grazing fish taken off each island this month at the rules as they stand, kg (`grazingCaughtKgMonth`, M315: within 3% of the death audit's). */
function caught(world: World): Array<{ island: ReefIsland; kg: number }> {
  const m = world.sanctuaries[0]?.management;
  return m ? REEF_ISLANDS.map((island) => ({ island, kg: grazingCaughtKgMonth(world, island, m) })) : [];
}

/** Nets washed ashore over `days` on an island's coast, tonnes (`ASHORE_T_PER_KM_YEAR`, NOAA's Hawaiʻi Large Debris Recovery, cited there). */
const ashore = (island: ReefIsland, days: number): number => (ASHORE_T_PER_KM_YEAR * COASTLINE_KM[island] * days) / 365;

/** A decision the month puts: its question, its way and cost, and what letting it be leaves. */
export interface FieldDecision {
  kind: 'season' | 'patrol' | 'nets' | 'count' | 'money' | 'finding';
  island: ReefIsland;
  question: string;
  way: { label: string; act: string; cost: string };
  leave: string;
}

/**
 * The decisions the world holds this month, most pressing first: the island whose grazing fish are taken most with
 * nothing protecting them (a patrol), the shore holding the most nets with no cleanup going (a cleanup), an island whose
 * step is its coral's count (a survey). Each only where it can be taken now.
 */
export function fieldDecisions(world: World): FieldDecision[] {
  const out: FieldDecision[] = [];
  const m = world.sanctuaries[0]?.management;
  const ops = world.operations;
  const today = epochDayOf(world);
  // The year's money short of what is owed before the grant, patrols running (M329: on a year whose boat ran trip on
  // trip, every other decision wanted money and the months to the grant put none): the patrol whose ending saves the
  // most before the grant ended early, a patrol paid by the day to its term's end (`patrolCost`; the year's money
  // counts it so, `yearMoney`), so the days not run are not paid. None where ending one saves under a month's (its
  // review: a patrol three days from its end had been offered as a month's saving).
  const year = yearMoney(world);
  // What the last survey found, its step (M330, O5): put where it can be taken now, as the others.
  const step = stepFromFindings(world);
  const all = { type: 'protect_herbivores' as const, island: 'all' as const, protect: true };
  const takeable = step && (step.kind === 'protect' ? freeLiaison(ops) !== undefined && ops.funds >= consultationCost(all)
    : step.kind === 'patrol' ? ops.funds >= 0 && nextStretch(world) !== null
      : planRoom(world) && year.spare >= surveyCost(step.kind === 'fishCount' ? 'reefFish' : 'reefBenthic', SURVEY_DAYS));
  // In the month its survey came home (its review: put first every month until taken, the rest never came).
  if (step && takeable && today - step.day <= MONTH_DAYS) {
    const cost = step.kind === 'protect' ? `${dollars(consultationCost(all))} and your liaison for its consultation`
      : step.kind === 'patrol' ? 'a patrol\'s months, paid by the day'
        : `${dollars(surveyCost(step.kind === 'fishCount' ? 'reefFish' : 'reefBenthic', SURVEY_DAYS))}, a boat ${SURVEY_DAYS} days`;
    out.push({ kind: 'finding', island: step.island ?? 'maui', question: `Your survey found: ${step.why}`, way: { label: step.label, act: step.act, cost }, leave: 'Let it be: the plan goes on as it was.' });
  }
  const days = year.grantDay - today;
  const saving = (managementOf(world)?.islands ?? []).filter((x) => x.patrol.level > 0).map((x) => {
    const left = x.patrol.until === null ? days : Math.max(0, Math.min(days, x.patrol.until - today));
    return { island: x.island, until: x.patrol.until, saved: ((x.patrol.costPerYear[x.patrol.level] ?? 0) * left) / 365, perMonth: patrolMonthly(x.patrol.costPerYear[x.patrol.level] ?? 0) };
  }).sort((a, b) => b.saved - a.saved)[0];
  if (year.spare < 0 && saving && saving.saved >= saving.perMonth) {
    const name = ISLAND_NAMES[saving.island];
    out.push({
      kind: 'money', island: saving.island,
      question: `The conservancy is ${dollars(-year.spare)} short of what it owes before the grant on ${dateLabel(year.grantDay)}; ending ${name}'s patrol saves the most.`,
      way: { label: `End ${name}'s patrol now`, act: `end-patrol:${saving.island}`, cost: `about ${dollars(saving.saved)} saved before the grant; its catch returns` },
      leave: `Let it be: the patrol runs ${saving.until === null ? 'until it is stopped' : `to ${dateLabel(saving.until)}`}, the year short by as much.`,
    });
  }
  // The warm season's own (M323, the casual year's H5): the season goal's way, a baseline count of the island with the
  // most shallow reef before July, or at NOAA's first alert the heat's choice (`goals.ts`'s `season`); the accounts'
  // way is no field decision.
  const season = goalsOf(world).find((g) => g.id === 'season');
  // A count only with room in the plan and its cost in hand, as the others (none the core would refuse, M322's review).
  const countable = planRoom(world) && ops.funds >= surveyCost('reefBenthic', SURVEY_DAYS);
  if (season?.way && season.level !== 'met' && season.way.act !== 'operations' && (!season.way.act.startsWith('reef:') || countable)) {
    const heat = season.title === 'Meet the heat';
    out.push({
      kind: 'season', island: mostExposed(world),
      question: `${season.title}: ${season.state}`,
      way: { label: season.way.label, act: season.way.act, cost: season.cost ?? (heat ? 'a bleaching check, or the plan kept' : '') },
      leave: heat ? 'Let it be: the heat\'s toll is left for the next coral survey to find.' : 'Let it be: the season\'s toll will have no count before it to be read against.',
    });
  }
  // The first test's count after the heat while it is the campaign's step (M325's review: in a year of heat its season
  // goal holds no way once the heat's choice is made, so November's card had put no count first), the season's island,
  // none while a coral count is out or planned.
  const after = currentObjective(world)?.objective.id === 'surveyAfter';
  const counting = ops.jobs.some((j) => j.survey === 'reefBenthic') || ops.plan.some((p) => p.kind === 'reefBenthic');
  if (after && !counting && countable && !out.some((d) => d.kind === 'season')) {
    const island = mostExposed(world);
    const name = ISLAND_NAMES[island];
    out.push({
      kind: 'season', island,
      question: `Survey the coral after the heat: only a count shows what the warm season did to ${name}'s coral, the most of its reef in the shallows.`,
      way: { label: 'Count the reef\'s coral', act: 'reef:survey:reefBenthic', cost: `${dollars(surveyCost('reefBenthic', SURVEY_DAYS))}, a boat ${SURVEY_DAYS} days` },
      leave: 'Let it be: the warm season\'s toll on the coral goes uncounted.',
    });
  }
  const yearEnd = epochDayFromCivil(civilFromEpochDay(today).year, 12, 31);
  // The coast the reef's patrol goes to (`nextStretch`, the card's own, so the coast named is the coast patrolled, M327's
  // review), asked only while nothing protects the grazing fish there: no rule in force or proposed for it.
  const stretch = m && ops.funds >= 0 ? nextStretch(world) : null;
  // No patrol asked in the first chapter, the second's choice (M333, O8's review).
  if (m && stretch && !inTutorial(world) && !m.herbivoreProtection[REEF_ISLANDS.indexOf(stretch.island)] && !protectionJob(ops, stretch.island)) {
    const name = ISLAND_NAMES[stretch.island];
    out.push({
      kind: 'patrol', island: stretch.island,
      question: `About ${roughly(stretch.now)} kg of ${name}'s grazing fish a month are being taken, more than off any coast not yet patrolled.`,
      way: { label: `Patrol ${name}'s coast`, act: 'reef:patrol', cost: `about ${roughly(stretch.fewer)} kg a month kept; a protected area for Maui Nui more, in months` },
      leave: `Let it be: about ${roughly(stretch.now)} kg a month go on being taken.`,
    });
  }
  const going = ops.jobs.some((j) => j.survey === 'shoreCleanup') || ops.plan.some((p) => p.kind === 'shoreCleanup');
  const shore = REEF_ISLANDS.map((island) => ({ island, t: shoreDebrisT(world, island) })).sort((a, b) => b.t - a.t)[0];
  // With room in the boats' plan, as the island's step asks (its review: a full plan refused the card's press).
  if (shore && !going && planRoom(world) && shore.t >= CLEARED_T_PER_DAY * SHORE_DAYS && year.spare >= surveyCost('shoreCleanup', SHORE_DAYS)) {
    const name = ISLAND_NAMES[shore.island];
    out.push({
      kind: 'nets', island: shore.island,
      question: `About ${Math.round(shore.t)} t of derelict nets lie on ${name}'s shore, the most of any.`,
      way: { label: `Clear ${name}'s shore`, act: 'reef:cleanup', cost: `${dollars(surveyCost('shoreCleanup', SHORE_DAYS))}, a boat ${SHORE_DAYS} days, ${(CLEARED_T_PER_DAY * SHORE_DAYS).toFixed(1)} t cleared` },
      leave: `Let it be: about ${ashore(shore.island, yearEnd - today).toFixed(1)} t more wash onto it by the year's end.`,
    });
  }
  // The reef's coral where the record shows a fall (M327, one reef: the decline goal's way, the whole reef's count).
  const count = goalsOf(world).find((g) => g.id === 'decline' && g.way);
  // With its cost in hand (its review: offered in debt, refused on its press).
  // Not the season's own count again under another question (its second review: Molokaʻi's count put twice in turn).
  if (count?.way && planRoom(world) && ops.funds >= surveyCost('reefBenthic', SURVEY_DAYS) && !out.some((d) => d.way.act === count.way?.act)) {
    out.push({
      kind: 'count', island: fallenFirst(world),
      question: count.state,
      way: { label: count.way.label, act: count.way.act, cost: count.cost ?? '' },
      leave: 'Let it be: the reef\'s coral stays unknown since its last count.',
    });
  }
  return out;
}

/** The island NOAA's record shows fallen furthest, the reef's count's reason (`coralVerdict`), Maui where none fell. */
function fallenFirst(world: World): ReefIsland {
  const drop = (island: ReefIsland): number => { const v = coralVerdict(world, island, true); return v.trend === 'down' && v.against && v.last ? v.against.value - v.last.value : 0; };
  return [...REEF_ISLANDS].sort((a, b) => drop(b) - drop(a))[0] ?? 'maui';
}

/** The month's decision: the decisions in turn, one a month, by the month's number, so the same one is not put every month. */
export function monthDecision(world: World, month: number): FieldDecision | null {
  const all = fieldDecisions(world);
  // The season's first at the heat's alert, and on the cards that close March to May, whose count comes home before July
  // (M323; its review: June's card is drawn on 30 June, its 10-day count home after the season began); and November's,
  // its count after the season home before the year's review, the first test's own "Survey the coral after the heat"
  // (M325, the casual year's H7: put in turn, it came on December's card, the fourth chapter never opened); else in turn.
  const season = all.find((d) => d.kind === 'season');
  const closing = ((month - 1) % 12) + 1;
  if (season && (season.question.startsWith('Meet the heat') || (closing >= 3 && closing <= 5) || closing === 11)) return season;
  // The money short next (M329; its review: never before the season's own months, November's count opening the fourth
  // chapter, M325).
  const money = all.find((d) => d.kind === 'money');
  if (money) return money;
  // Then what the last survey found (M330, O5).
  const finding = all.find((d) => d.kind === 'finding');
  if (finding) return finding;
  return all.length === 0 ? null : (all[month % all.length] as FieldDecision);
}

/** The water's heat in a line, the season's months (July to October closing) or wherever heat stress builds. */
function heatLine(world: World, month: number): string {
  const s = world.sanctuaries[0];
  if (!s) return '';
  const dhw = degreeHeatingWeeks(s.heatStress);
  const peak = s.heatStress.peakDhw;
  const closing = ((month - 1) % 12) + 1;
  if (dhw <= 0 && peak <= 0 && (closing < 7 || closing > 10)) return '';
  // Rounded down, so it never reads as the alert below it (its review: 3.96 as "4.0 ... comes at 4").
  const at = (x: number): string => (Math.floor(x * 10) / 10).toFixed(1);
  // The alert as the episode reached it, at its peak, the decision under it the same (its review: today's fallen
  // figure had read "comes at 4" over "Heat stress has reached NOAA's first alert").
  if (peak >= HEAT_ALERT_DHW) return `The heat stress this episode reached ${at(peak)} °C-weeks, past NOAA's first bleaching alert at ${HEAT_ALERT_DHW}; it stands at ${at(dhw)} now.`;
  // Today's figure alone, zero or not (its second review: "no heat stress this season" had been read from today's zero,
  // though heat below the alert leaves the 12 weeks' sum by October and a marine heatwave can come with none); the
  // season's end by its month, the card drawn again on a later day (the same review: "ends today" read on 3 November).
  const ends = closing === 10 ? ' The warm season ends with October.' : '';
  return `The water's heat stress stands at ${at(dhw)} °C-weeks; NOAA's first bleaching alert comes at ${HEAT_ALERT_DHW}.${ends}`;
}

/** The month in the field, at its close: what it did on the water, then its decision. `days` the month's length. */
export function fieldMonthHtml(world: World, month: number, days: number): string {
  const taken = caught(world);
  const total = taken.reduce((n, x) => n + x.kg, 0);
  const most = [...taken].sort((a, b) => b.kg - a.kg)[0];
  const nets = REEF_ISLANDS.reduce((n, island) => n + ashore(island, days), 0);
  const lines = [
    // The fishing's rate at today's fish and rules, a month's (its review: "were taken" had told an estimate as a count).
    most && total > 0 ? `About ${roughly(total)} kg of grazing fish a month are being taken off Maui Nui's reefs, the most off ${ISLAND_NAMES[most.island]} (${roughly(most.kg)} kg).` : '',
    `About ${nets.toFixed(1)} t of derelict nets washed onto its shores.`,
    // The water's heat, through the warm season's months and whenever it is stressed (M323): NOAA Coral Reef Watch's
    // Degree Heating Weeks, its first alert at 4 (`HEAT_ALERT_DHW`).
    heatLine(world, month),
    // The year's money against what is owed to the next grant (M324, the casual year's H6: "Money stops mattering on 1
    // July"), so the decision under it reads as a trade.
    spareWords(world),
  ].filter(Boolean);
  const d = monthDecision(world, month);
  const decision = d ? `<h4>This month's decision</h4><p>${escapeHtml(d.question)}</p>
      <div class="home-actions"><button class="primary" data-act="goal" data-goal="${escapeHtml(d.way.act)}"><strong>${escapeHtml(d.way.label)}</strong><span>${escapeHtml(d.way.cost)}</span></button>
      <button data-act="month-continue"><strong>Let it be</strong><span>${escapeHtml(d.leave.replace(/^Let it be: /, ''))}</span></button></div>` : '';
  return `<section class="month-field"><h4>In the field</h4>${lines.map((l) => `<p>${escapeHtml(l)}</p>`).join('')}${decision}</section>`;
}
